// src/services/youtubeService.ts
const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY2;
const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID;
const VIDEO_DETAILS_CACHE_TTL_MS = 365 * 24 * 60 * 60 * 1000;
const CHANNEL_DETAILS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  publishedAt: string;
  duration: string;
}

export interface YouTubeChannelDetails {
  id: string;
  title: string;
  thumbnail: string;
  subscriberCount: number | null;
}

interface YouTubeVideoDetails {
  title: string;
  thumbnail: string;
}

interface CachedYouTubeData<T> {
  cachedAt: number;
  data: T;
}

const videoDetailsRequests = new Map<string, Promise<YouTubeVideoDetails | null>>();

const isVideoDetails = (value: unknown): value is YouTubeVideoDetails => {
  if (!value || typeof value !== 'object') return false;

  const details = value as YouTubeVideoDetails;
  return typeof details.title === 'string' && typeof details.thumbnail === 'string';
};

const isChannelDetails = (value: unknown): value is YouTubeChannelDetails => {
  if (!value || typeof value !== 'object') return false;

  const details = value as YouTubeChannelDetails;
  return (
    typeof details.id === 'string' &&
    typeof details.title === 'string' &&
    typeof details.thumbnail === 'string' &&
    (typeof details.subscriberCount === 'number' || details.subscriberCount === null)
  );
};

const isChannelDetailsMap = (value: unknown): value is Record<string, YouTubeChannelDetails> => {
  if (!value || typeof value !== 'object') return false;

  return Object.values(value).every(isChannelDetails);
};

const getChannelCacheKey = (channelIds: string[]): string =>
  `youtube-channel-details:v2:${[...channelIds].sort().join(',')}`;

const hasRequestedChannelDetails = (
  data: Record<string, YouTubeChannelDetails>,
  channelIds: string[]
): boolean =>
  channelIds.every(channelId => Boolean(data[channelId]?.thumbnail));

const getCachedVideoDetails = (videoId: string, allowExpired = false): YouTubeVideoDetails | null => {
  try {
    if (typeof window === 'undefined') return null;

    const cached = window.localStorage.getItem(`youtube-video-details:${videoId}`);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as CachedYouTubeData<YouTubeVideoDetails>;
    const isExpired = Date.now() - parsed.cachedAt > VIDEO_DETAILS_CACHE_TTL_MS;

    if (!allowExpired && isExpired) return null;
    if (!isVideoDetails(parsed.data)) return null;

    return parsed.data;
  } catch {
    return null;
  }
};

const getCachedChannelDetails = (
  channelIds: string[],
  allowExpired = false
): Record<string, YouTubeChannelDetails> | null => {
  try {
    if (typeof window === 'undefined') return null;

    const cached = window.localStorage.getItem(getChannelCacheKey(channelIds));
    if (!cached) return null;

    const parsed = JSON.parse(cached) as CachedYouTubeData<Record<string, YouTubeChannelDetails>>;
    const isExpired = Date.now() - parsed.cachedAt > CHANNEL_DETAILS_CACHE_TTL_MS;

    if (!allowExpired && isExpired) return null;
    if (!isChannelDetailsMap(parsed.data)) return null;
    if (!allowExpired && !hasRequestedChannelDetails(parsed.data, channelIds)) return null;

    return parsed.data;
  } catch {
    return null;
  }
};

const cacheVideoDetails = (videoId: string, data: YouTubeVideoDetails): void => {
  try {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(
      `youtube-video-details:${videoId}`,
      JSON.stringify({
        cachedAt: Date.now(),
        data,
      })
    );
  } catch {
    // Storage may be unavailable in private browsing or quota-constrained browsers.
  }
};

const cacheChannelDetails = (
  channelIds: string[],
  data: Record<string, YouTubeChannelDetails>
): void => {
  try {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(
      getChannelCacheKey(channelIds),
      JSON.stringify({
        cachedAt: Date.now(),
        data,
      })
    );
  } catch {
    // Storage may be unavailable in private browsing or quota-constrained browsers.
  }
};

export const youtubeService = {
  async getLatestVideo(): Promise<YouTubeVideo | null> {
    try {
      const searchResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/search?` +
        `key=${YOUTUBE_API_KEY}&` +
        `channelId=${CHANNEL_ID}&` +
        `part=snippet&` +
        `order=date&` +
        `type=video&` +
        `videoEmbeddable=true&` +
        `maxResults=10`
      );

      const searchData = await searchResponse.json();

      if (!searchData.items?.length) return null;

      const videoIds = searchData.items.map((item: any) => item.id.videoId).join(',');

      const videoResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?` +
        `key=${YOUTUBE_API_KEY}&` +
        `id=${videoIds}&` +
        `part=contentDetails,snippet,statistics`
      );

      const videoData = await videoResponse.json();

      const longFormVideo = videoData.items.find((video: any) => {
        const duration = parseDuration(video.contentDetails.duration);
        return duration >= 60;
      });

      if (!longFormVideo) return null;

      return {
        id: longFormVideo.id,
        title: longFormVideo.snippet.title,
        description: longFormVideo.snippet.description,
        thumbnail: longFormVideo.snippet.thumbnails.high.url,
        publishedAt: longFormVideo.snippet.publishedAt,
        duration: longFormVideo.contentDetails.duration
      };
    } catch {
      return null;
    }
  },

  async getVideoDetails(videoId: string): Promise<YouTubeVideoDetails | null> {
    const cachedDetails = getCachedVideoDetails(videoId);
    if (cachedDetails) return cachedDetails;

    const existingRequest = videoDetailsRequests.get(videoId);
    if (existingRequest) return existingRequest;

    const request = (async () => {
      try {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?` +
          `key=${YOUTUBE_API_KEY}&` +
          `id=${videoId}&` +
          `part=snippet`
        );

        const data = await response.json();
        if (!response.ok || data.error) {
          throw new Error('Unable to fetch YouTube video details.');
        }
        if (!data.items?.[0]) return null;

        const video = data.items[0];
        const details = {
          title: video.snippet.title,
          thumbnail: video.snippet.thumbnails.maxres?.url || video.snippet.thumbnails.high.url
        };

        cacheVideoDetails(videoId, details);
        return details;
      } catch {
        return getCachedVideoDetails(videoId, true);
      } finally {
        videoDetailsRequests.delete(videoId);
      }
    })();

    videoDetailsRequests.set(videoId, request);
    return request;
  },

  cacheVideoDetails(videoId: string, details: YouTubeVideoDetails): void {
    cacheVideoDetails(videoId, details);
  },

  async getChannelDetails(channelIds: string[]): Promise<Record<string, YouTubeChannelDetails>> {
    const uniqueChannelIds = [...new Set(channelIds.filter(Boolean))];
    if (!uniqueChannelIds.length) return {};

    const cachedDetails = getCachedChannelDetails(uniqueChannelIds);
    if (cachedDetails) return cachedDetails;

    try {
      if (!YOUTUBE_API_KEY) {
        throw new Error('Missing YouTube API key.');
      }

      const params = new URLSearchParams({
        key: YOUTUBE_API_KEY,
        id: uniqueChannelIds.join(','),
        part: 'snippet,statistics',
        maxResults: '50',
      });
      const response = await fetch(`https://www.googleapis.com/youtube/v3/channels?${params}`);
      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error('Unable to fetch YouTube channel details.');
      }

      const details = (data.items || []).reduce(
        (channels: Record<string, YouTubeChannelDetails>, channel: any) => {
          const thumbnails = channel.snippet?.thumbnails || {};
          const rawSubscriberCount = channel.statistics?.hiddenSubscriberCount
            ? null
            : Number(channel.statistics?.subscriberCount);
          const subscriberCount = Number.isFinite(rawSubscriberCount) ? rawSubscriberCount : null;

          channels[channel.id] = {
            id: channel.id,
            title: channel.snippet?.title || '',
            thumbnail: thumbnails.high?.url || thumbnails.medium?.url || thumbnails.default?.url || '',
            subscriberCount,
          };

          return channels;
        },
        {}
      );

      if (!Object.keys(details).length) {
        throw new Error('YouTube channel response did not include channel details.');
      }

      cacheChannelDetails(uniqueChannelIds, details);
      return details;
    } catch {
      return getCachedChannelDetails(uniqueChannelIds, true) || {};
    }
  }
};

function parseDuration(duration: string): number {
  const matches = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);

  if (!matches) return 0;

  const [, hours, minutes, seconds] = matches;

  return (
    (parseInt(hours || '0') * 3600) +
    (parseInt(minutes || '0') * 60) +
    parseInt(seconds || '0')
  );
}

export default youtubeService;
